import { useState, useCallback } from "react"
import { v4 as uuidv4 } from "uuid"
import { Participant, isValidEmail } from "../library/Participant"
import { isValidPhoneNumber } from "react-phone-number-input"

function storageKey(presetCode: string): string {
  return `preset_${presetCode}_participant`
}

function loadFromStorage(presetCode: string): Participant {
  const key = storageKey(presetCode)
  const raw = sessionStorage.getItem(key)
  if (raw) {
    try {
      const parsed = JSON.parse(raw)
      return {
        sessionId: parsed.sessionId ?? uuidv4(),
        email: parsed.email ?? null,
        firstName: parsed.firstName ?? null,
        lastName: parsed.lastName ?? null,
        phoneNumber: parsed.phoneNumber ?? null,
        acceptedTerms: parsed.acceptedTerms ? new Date(parsed.acceptedTerms) : null,
      }
    } catch {
      // Fall through to create new
    }
  }
  return {
    sessionId: uuidv4(),
    email: null,
    firstName: null,
    lastName: null,
    phoneNumber: null,
    acceptedTerms: null,
  }
}

function mergeFromSearchParams(participant: Participant): Participant {
  const params = new URLSearchParams(window.location.search)
  const updated = { ...participant }

  const sessionId = params.get("sessionId")
  const email = params.get("email")
  const firstName = params.get("firstName")
  const lastName = params.get("lastName")
  const phoneNumber = params.get("phoneNumber")

  if (sessionId && sessionId.length >= 16) {
    updated.sessionId = sessionId
    params.delete("sessionId")
  }

  if (email && isValidEmail(email)) {
    updated.email = email
    params.delete("email")
  }

  if (firstName) {
    updated.firstName = firstName
    params.delete("firstName")
  }

  if (lastName) {
    updated.lastName = lastName

    // Sanitize the last name field for Nemours
    if (lastName.includes("?")) {
      updated.lastName = lastName.split("?")[0]
    }

    params.delete("lastName")
  }

  if (phoneNumber && isValidPhoneNumber(phoneNumber)) {
    updated.phoneNumber = phoneNumber
    params.delete("phoneNumber")
  }

  // Replace search params with a list that omits the ones that have been handled
  let newUrl = window.location.pathname
  if (params.toString().length > 0) {
    newUrl += "?" + params.toString()
  }
  window.history.replaceState(null, document.title, newUrl)

  return updated
}

function saveToStorage(presetCode: string, participant: Participant) {
  sessionStorage.setItem(storageKey(presetCode), JSON.stringify(participant))
}

interface UseParticipantResult {
  participant: Participant
  setParticipant: (participant: Participant) => void
}

export function useParticipant(presetCode: string): UseParticipantResult {
  const [participant, setParticipantState] = useState<Participant>(() => {
    const loaded = loadFromStorage(presetCode)
    const merged = mergeFromSearchParams(loaded)
    saveToStorage(presetCode, merged)
    return merged
  })

  const setParticipant = useCallback(
    (newParticipant: Participant) => {
      saveToStorage(presetCode, newParticipant)
      setParticipantState(newParticipant)
    },
    [presetCode],
  )

  return { participant, setParticipant }
}
